import { BadImplementationException } from '../core/exceptions/exceptions.js';
import { Logger } from '../core/logger/index.js';
import { isPromise } from './promise-tools.js';
import { exists } from './tools.js';
/**
 * Debounce implementation with support for async actions and max wait time.
 *
 * Debouncing delays the execution of an action until after a specified wait period
 * has elapsed since the last trigger. If triggers continue to occur, the wait timer
 * resets. Optionally supports a maximum wait time that forces execution even if
 * triggers continue.
 *
 * **Features**:
 * - Standard debounce behavior (resets timer on each trigger)
 * - Optional max wait time (throttle-like behavior)
 * - Handles async actions (waits for completion before processing next trigger)
 * - Queues triggers that occur while action is executing
 * - Can flush immediately or clear scheduled execution
 *
 * **Behavior with async actions**: If the action returns a Promise, the debounce
 * waits for it to complete before processing the next trigger. If triggers occur
 * during execution, they are queued and executed immediately after completion.
 *
 * @template Args - Type of arguments passed to the action
 * @template Response - Return type of the action
 */
export class Debounce extends Logger {
    /** The function to debounce */
    action;
    /** Wait time in milliseconds before executing action */
    waitMs;
    /** Optional maximum wait time - forces execution even if triggers continue */
    maxWaitMs;
    /** Incrementing index to track trigger generations and prevent stale executions */
    triggerIndex = 0;
    /** Timer for the wait period */
    waitTimer;
    /** Timer for the max wait period */
    maxTimer;
    /** Latest arguments from the most recent trigger */
    latestArgs = [];
    /** Flag indicating if the action is currently executing (for async actions) */
    actionInProgress = false;
    /** Flag indicating if a trigger occurred while action was executing */
    triggerPending = false;
    /**
     * Creates a new Debounce instance.
     *
     * @param action - Function to debounce
     * @param waitMs - Wait time in milliseconds (must be >= 0)
     * @param maxWaitMs - Optional maximum wait time (must be > 0 and >= waitMs)
     */
    constructor(action, waitMs, maxWaitMs) {
        super('Debounce');
        this.validate_WaitMs(waitMs);
        this.validate_MaxWaitMs(waitMs, maxWaitMs);
        this.action = action;
        this.waitMs = waitMs;
        this.maxWaitMs = maxWaitMs;
    }
    //######################### Validation #########################
    validate_WaitMs(waitMs) {
        if (waitMs < 0)
            throw new BadImplementationException('waitMs value must be >= 0');
    }
    validate_MaxWaitMs(waitMs, maxWaitMs) {
        if (!exists(maxWaitMs))
            return;
        if (maxWaitMs <= 0)
            throw new BadImplementationException('maxWaitMs value must be > 0');
        if (maxWaitMs < waitMs)
            this.logWarning('maxWaitMs < waitMs; the \'max\' may never matter');
    }
    //######################### Internal Logic #########################
    clearTimers() {
        if (this.waitTimer) {
            clearTimeout(this.waitTimer);
            this.waitTimer = undefined;
        }
        if (this.maxTimer) {
            clearTimeout(this.maxTimer);
            this.maxTimer = undefined;
        }
    }
    executeAction(triggerIndex, immediate) {
        if (triggerIndex !== this.triggerIndex)
            return;
        this.triggerIndex++;
        const args = this.latestArgs ?? [];
        this.clearTimers();
        const result = this.action(...args);
        if (immediate)
            return result;
        //Set up chain triggering if the action is asynchronous.
        if (isPromise(result)) {
            this.actionInProgress = true;
            Promise.resolve(result).finally(() => {
                this.actionInProgress = false;
                //Immediately execute the action again if debounce was triggered during the current run
                if (this.triggerPending) {
                    this.triggerPending = false;
                    void this.executeAction(this.triggerIndex);
                }
            });
        }
    }
    //######################### Public Logic #########################
    /**
     * Triggers the debounced action.
     *
     * Schedules the action to execute after `waitMs` milliseconds. If called again
     * before execution, the timer resets. If `maxWaitMs` is set and reached, the
     * action executes regardless of recent triggers.
     *
     * **Behavior**:
     * - If action is currently executing (async), queues a trigger for after completion
     * - Otherwise, resets the wait timer
     * - Sets max wait timer on first trigger (if configured)
     *
     * @param args - Arguments to pass to the action
     */
    trigger(...args) {
        this.latestArgs = args;
        //If action is in progress, queue up immediate trigger
        if (this.actionInProgress) {
            this.triggerPending = true;
            return;
        }
        // Normal debounce behavior while idle:
        //Reset waitTimer
        if (this.waitTimer)
            clearTimeout(this.waitTimer);
        const triggerIndex = this.triggerIndex;
        this.waitTimer = setTimeout(() => {
            void this.executeAction(triggerIndex);
        }, this.waitMs);
        //Set maxWaitTimer if configured and does not currently exist
        if (exists(this.maxWaitMs) && !exists(this.maxTimer))
            this.maxTimer = setTimeout(() => {
                void this.executeAction(triggerIndex);
            }, this.maxWaitMs);
    }
    /**
     * Checks if an action is currently scheduled for execution.
     *
     * @returns true if either wait timer or max timer is active
     */
    isScheduled() {
        return exists(this.waitTimer ?? this.maxTimer);
    }
    /**
     * Executes the action immediately if scheduled, returning the result.
     *
     * Cancels any pending timers and executes the action with the latest arguments.
     * If the action is async, the Promise is returned and the caller must handle it.
     *
     * **Note**: If nothing is scheduled, logs an info message and returns undefined.
     *
     * @returns Action result if scheduled, undefined otherwise
     * @throws If action throws synchronously, the error propagates to caller
     */
    flush() {
        if (!this.isScheduled())
            return void this.logInfo('Not scheduled');
        return this.executeAction(this.triggerIndex, true);
    }
    /**
     * Cancels any scheduled execution.
     *
     * Clears timers and increments trigger index to invalidate any pending executions.
     * Does not cancel in-flight async actions - they will complete normally.
     */
    clear() {
        this.clearTimers();
        this.triggerIndex++;
        this.triggerPending = false;
    }
}
//# sourceMappingURL=debounce.js.map